/**
 * EntityCommentsTab — the Discussion panel on an item, cycle, track, session
 * or tool. One component, one table (`entity_comments`), every page.
 *
 * What a person can do here:
 *   - write a comment in Markdown (bold, lists, links) and name people with @;
 *     the people named are told (in the app, on the phone, and in Slack where
 *     the workspace has it — the server does that, see migration 815)
 *   - reply in a thread (one level), and resolve or reopen a thread when the
 *     question it raised is answered — any member, not only the author
 *   - comment on one attribute value rather than the whole item: the Values
 *     tab passes the anchor in the address (?anchor=attribute:<key>:<label>)
 *   - land on one comment from a notification (?comment=<id>)
 */

import {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  type ChangeEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react'
import { useSearchParams } from 'react-router-dom'
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkBreaks from 'remark-breaks'
import {
  Send,
  Pencil,
  X,
  MessageSquare,
  Reply,
  Check,
  RotateCcw,
  Bold,
  Italic,
  List,
  Link2,
  AtSign,
  Tag,
} from 'lucide-react'
import {
  useEntityCommentsStore,
  type EntityComment,
  type EntityCommentEntityType,
  type CommentAnchor,
} from '@/stores/prioritization/entityCommentsStore'
import { useUserStore } from '@/stores/prioritization/userStore'
import { listWorkspaceMembers } from '@/lib/workspaceService'
import { useActorDisplayMap } from '@/hooks/prioritization/useActorDisplayMap'
import {
  anchorFromParam,
  bodyToEditable,
  bodyToMarkdown,
  filterMentionCandidates,
  mentionDisplayName,
  mentionQueryAtCaret,
  serializeMentions,
  MENTION_HREF_PREFIX,
  type MentionCandidate,
} from '@/lib/commentMentions'
import { Button } from '@/components/ui/Button'
import { Avatar } from '@/components/ds'
import { VoiceBadge } from '@/features/voices'
import { CreationProvenanceBadge } from '../provenance/CreationProvenanceBadge'

// ============================================================================
// Props
// ============================================================================

export interface EntityCommentsTabProps {
  entityType: EntityCommentEntityType
  entityId: string
  workspaceId: string
  canComment?: boolean
  className?: string
  /** Start the composer on one value ("about Impact"). The address's ?anchor= wins when both are present. */
  anchor?: CommentAnchor | null
}

type ThreadFilter = 'open' | 'resolved' | 'all'

// ============================================================================
// Small pieces
// ============================================================================

function formatAge(date: Date | string): string {
  const diff = Date.now() - new Date(date).getTime()
  const m = Math.floor(diff / 60_000)
  const h = Math.floor(diff / 3_600_000)
  const d = Math.floor(diff / 86_400_000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  if (h < 24) return `${h}h ago`
  if (d < 7) return `${d}d ago`
  return new Date(date).toLocaleDateString()
}

function AnchorChip({ anchor, onClear }: { anchor: CommentAnchor; onClear?: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 border border-[var(--rule)] bg-[var(--surface)] px-2 py-0.5 text-[11px] text-[var(--text-sec)]">
      <Tag className="h-3 w-3" aria-hidden />
      On {anchor.label ?? anchor.key}
      {onClear && (
        <button
          type="button"
          onClick={onClear}
          className="ml-0.5 text-[var(--text-ter)] hover:text-[var(--text-pri)]"
          aria-label="Comment on the whole item instead"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  )
}

/** A comment body: Markdown, with each named person shown as a chip. */
function CommentBody({ body }: { body: string }) {
  const markdown = useMemo(() => bodyToMarkdown(body), [body])
  return (
    <div className="text-sm leading-relaxed text-[var(--text-sec)] break-words [&_p]:my-1 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0 [&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5 [&_code]:bg-[var(--surface)] [&_code]:px-1 [&_code]:text-[13px] [&_pre]:my-1 [&_pre]:overflow-x-auto [&_pre]:bg-[var(--surface)] [&_pre]:p-2 [&_blockquote]:my-1 [&_blockquote]:border-l-2 [&_blockquote]:border-[var(--rule)] [&_blockquote]:pl-3 [&_strong]:text-[var(--text-pri)]">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        // The default transform drops unknown schemes; a mention is ours, everything else stays sanitised.
        urlTransform={(url) => (url.startsWith(MENTION_HREF_PREFIX) ? url : defaultUrlTransform(url))}
        components={{
          a: (props) => {
            const href = typeof props.href === 'string' ? props.href : ''
            if (href.startsWith(MENTION_HREF_PREFIX)) {
              return (
                <span
                  className="inline-flex items-center bg-[var(--hover-tint)] px-1 font-medium text-[var(--text-pri)]"
                  data-user-id={href.slice(MENTION_HREF_PREFIX.length)}
                >
                  {props.children}
                </span>
              )
            }
            return (
              <a href={href} target="_blank" rel="noreferrer" className="text-[var(--text-pri)] underline">
                {props.children}
              </a>
            )
          },
          h1: (props) => <p className="font-semibold text-[var(--text-pri)]">{props.children}</p>,
          h2: (props) => <p className="font-semibold text-[var(--text-pri)]">{props.children}</p>,
          h3: (props) => <p className="font-semibold text-[var(--text-pri)]">{props.children}</p>,
          h4: (props) => <p className="font-semibold text-[var(--text-pri)]">{props.children}</p>,
          h5: (props) => <p className="font-semibold text-[var(--text-pri)]">{props.children}</p>,
          h6: (props) => <p className="font-semibold text-[var(--text-pri)]">{props.children}</p>,
          img: () => null,
        }}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  )
}

// ============================================================================
// Composer — Markdown, @ names, one anchor
// ============================================================================

interface ComposerProps {
  inputId: string
  members: MentionCandidate[]
  placeholder: string
  submitLabel: string
  initialText?: string
  initialPicked?: MentionCandidate[]
  anchor?: CommentAnchor | null
  onClearAnchor?: () => void
  autoFocus?: boolean
  rows?: number
  onSubmit: (text: string, picked: MentionCandidate[]) => void
  onCancel?: () => void
}

function Composer({
  inputId,
  members,
  placeholder,
  submitLabel,
  initialText = '',
  initialPicked = [],
  anchor,
  onClearAnchor,
  autoFocus,
  rows = 2,
  onSubmit,
  onCancel,
}: ComposerProps) {
  const [text, setText] = useState(initialText)
  const [picked, setPicked] = useState<MentionCandidate[]>(initialPicked)
  const [menu, setMenu] = useState<{ query: string; start: number; index: number } | null>(null)
  const ref = useRef<HTMLTextAreaElement>(null)

  const candidates = useMemo(
    () => (menu ? filterMentionCandidates(members, menu.query) : []),
    [members, menu],
  )

  const refreshMenu = useCallback((value: string, caret: number) => {
    const q = mentionQueryAtCaret(value, caret)
    setMenu(q ? { ...q, index: 0 } : null)
  }, [])

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value)
    refreshMenu(e.target.value, e.target.selectionStart ?? e.target.value.length)
  }

  const placeCaret = (pos: number) => {
    requestAnimationFrame(() => {
      const el = ref.current
      if (!el) return
      el.focus()
      el.setSelectionRange(pos, pos)
    })
  }

  const pick = (person: MentionCandidate) => {
    if (!menu) return
    const caret = ref.current?.selectionStart ?? text.length
    const before = text.slice(0, menu.start)
    const after = text.slice(caret)
    const next = `${before}@${person.name} ${after}`
    setText(next)
    setPicked((p) => (p.some((x) => x.id === person.id) ? p : [...p, person]))
    setMenu(null)
    placeCaret(before.length + person.name.length + 2)
  }

  const submit = () => {
    const trimmed = text.trim()
    if (!trimmed) return
    onSubmit(trimmed, picked)
    setText('')
    setPicked([])
    setMenu(null)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (menu && candidates.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setMenu((m) => (m ? { ...m, index: (m.index + 1) % candidates.length } : m))
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setMenu((m) => (m ? { ...m, index: (m.index - 1 + candidates.length) % candidates.length } : m))
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        pick(candidates[menu.index])
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setMenu(null)
        return
      }
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      submit()
      return
    }
    if (e.key === 'Escape' && onCancel) onCancel()
  }

  /** Wrap the selection (or drop a placeholder) in Markdown marks. */
  const wrap = (before: string, after: string, fallback: string) => {
    const el = ref.current
    const start = el?.selectionStart ?? text.length
    const end = el?.selectionEnd ?? text.length
    const selected = text.slice(start, end) || fallback
    const next = `${text.slice(0, start)}${before}${selected}${after}${text.slice(end)}`
    setText(next)
    const from = start + before.length
    requestAnimationFrame(() => {
      const node = ref.current
      if (!node) return
      node.focus()
      node.setSelectionRange(from, from + selected.length)
    })
  }

  const startList = () => {
    const el = ref.current
    const start = el?.selectionStart ?? text.length
    const lineStart = text.lastIndexOf('\n', start - 1) + 1
    const prefix = text.slice(lineStart, start).trim() === '' ? '- ' : '\n- '
    const next = `${text.slice(0, start)}${prefix}${text.slice(start)}`
    setText(next)
    placeCaret(start + prefix.length)
  }

  const startMention = () => {
    const el = ref.current
    const start = el?.selectionStart ?? text.length
    const needsSpace = start > 0 && !/\s/.test(text[start - 1])
    const insert = `${needsSpace ? ' ' : ''}@`
    const next = `${text.slice(0, start)}${insert}${text.slice(start)}`
    setText(next)
    const caret = start + insert.length
    placeCaret(caret)
    refreshMenu(next, caret)
  }

  const toolButton = (label: string, icon: ReactNode, onClick: () => void) => (
    <button
      type="button"
      onClick={onClick}
      onMouseDown={(e) => e.preventDefault()}
      title={label}
      aria-label={label}
      className="p-1 text-[var(--text-ter)] hover:bg-[var(--hover-tint)] hover:text-[var(--text-pri)]"
    >
      {icon}
    </button>
  )

  return (
    <div className="space-y-2">
      {anchor && <AnchorChip anchor={anchor} onClear={onClearAnchor} />}
      <div className="border border-[var(--rule)] bg-[var(--bg-raised)] focus-within:border-[var(--text-ter)]">
        <div className="flex items-center gap-0.5 border-b border-[var(--rule)] px-1 py-0.5">
          {toolButton('Bold', <Bold className="h-3.5 w-3.5" />, () => wrap('**', '**', 'bold'))}
          {toolButton('Italic', <Italic className="h-3.5 w-3.5" />, () => wrap('_', '_', 'italic'))}
          {toolButton('List', <List className="h-3.5 w-3.5" />, startList)}
          {toolButton('Link', <Link2 className="h-3.5 w-3.5" />, () => wrap('[', '](https://)', 'link text'))}
          {toolButton('Name someone', <AtSign className="h-3.5 w-3.5" />, startMention)}
        </div>
        <div className="relative">
          <textarea
            id={inputId}
            ref={ref}
            value={text}
            rows={rows}
            autoFocus={autoFocus}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onClick={(e) => refreshMenu(text, e.currentTarget.selectionStart ?? text.length)}
            onBlur={() => setTimeout(() => setMenu(null), 120)}
            placeholder={placeholder}
            aria-autocomplete="list"
            aria-expanded={!!menu && candidates.length > 0}
            className="w-full resize-y bg-transparent px-3 py-2 text-sm text-[var(--text-pri)] placeholder:text-[var(--text-ter)] focus:outline-none"
          />
          {menu && candidates.length > 0 && (
            <ul
              role="listbox"
              aria-label="People to name"
              className="absolute left-2 top-full z-20 mt-1 w-72 border border-[var(--rule)] bg-[var(--bg-raised)] py-1 shadow-lg"
            >
              {candidates.map((person, i) => (
                <li
                  key={person.id}
                  role="option"
                  aria-selected={i === menu.index}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    pick(person)
                  }}
                  onMouseEnter={() => setMenu((m) => (m ? { ...m, index: i } : m))}
                  className={`flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm ${
                    i === menu.index ? 'bg-[var(--hover-tint)] text-[var(--text-pri)]' : 'text-[var(--text-sec)]'
                  }`}
                >
                  <Avatar name={person.name} size="xs" />
                  <span className="truncate">{person.name}</span>
                  {person.email && (
                    <span className="ml-auto truncate text-[11px] text-[var(--text-ter)]">{person.email}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] text-[var(--text-ter)]">@ names someone · ⌘↵ posts</p>
        <div className="flex gap-2">
          {onCancel && (
            <Button variant="ghost" size="sm" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button variant="cta" size="sm" onClick={submit} disabled={!text.trim()}>
            <Send className="h-3.5 w-3.5" />
            {submitLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// One comment (root or reply)
// ============================================================================

interface CommentRowProps {
  comment: EntityComment
  isReply?: boolean
  userId: string
  canComment: boolean
  members: MentionCandidate[]
  resolverName?: string | null
  highlighted: boolean
  editing: boolean
  onStartEdit: (id: string) => void
  onCancelEdit: () => void
  onSaveEdit: (comment: EntityComment, text: string, picked: MentionCandidate[]) => void
  onDelete: (id: string) => void
  onReply?: () => void
  onToggleResolved?: () => void
}

function CommentRow({
  comment,
  isReply = false,
  userId,
  canComment,
  members,
  resolverName,
  highlighted,
  editing,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
  onReply,
  onToggleResolved,
}: CommentRowProps) {
  // An agent's comment (migration 955) names the person who made the key in
  // authorId as the accountable person, not as its author: that person does not
  // get Edit or Delete on it (the database refuses both as well, migration 958).
  const isAgent = comment.authorPrincipalType === 'agent'
  const isOwn = comment.authorId === userId && !isAgent
  const isVoice =
    comment.authorPrincipalType === 'voice' ||
    /\(Voice\)/i.test(comment.authorName) ||
    comment.body.trimStart().startsWith('[Voice')
  const resolved = !isReply && !!comment.resolvedAt
  const editable = useMemo(() => (editing ? bodyToEditable(comment.body) : null), [editing, comment.body])
  const anchor: CommentAnchor | null =
    comment.anchorType === 'attribute' && comment.anchorKey
      ? { type: 'attribute', key: comment.anchorKey, label: comment.anchorLabel ?? undefined }
      : null

  const action = (label: string, icon: ReactNode, onClick: () => void, tone: 'quiet' | 'danger' = 'quiet') => (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1 text-xs ${
        tone === 'danger'
          ? 'text-[var(--text-ter)] hover:text-[var(--coral)]'
          : 'text-[var(--text-ter)] hover:text-[var(--text-pri)]'
      }`}
    >
      {icon}
      {label}
    </button>
  )

  return (
    <div
      id={`comment-${comment.id}`}
      className={`flex gap-3 transition-shadow ${highlighted ? 'ring-2 ring-[var(--gold)] ring-offset-2 ring-offset-[var(--bg-raised)]' : ''}`}
    >
      <Avatar name={comment.authorName || 'Someone'} size={isReply ? 'sm' : 'md'} className="mt-0.5 flex-shrink-0" />

      <div className="min-w-0 flex-1">
        <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className={`text-sm font-medium ${resolved ? 'text-[var(--text-sec)]' : 'text-[var(--text-pri)]'}`}>
            {comment.authorName}
          </span>
          {isVoice && <VoiceBadge />}
          {(isVoice || isAgent) && (
            <CreationProvenanceBadge
              source={comment.creationSource ?? 'ai'}
              reviewStatus={comment.creationReviewStatus ?? 'none'}
              provenance={comment.creationProvenance}
              variant="label"
            />
          )}
          <span className="text-xs text-[var(--text-ter)]">{formatAge(comment.createdAt)}</span>
          {comment.isEdited && <span className="text-xs text-[var(--text-ter)]">(edited)</span>}
          {anchor && <AnchorChip anchor={anchor} />}
          {resolved && (
            <span className="inline-flex items-center gap-1 text-xs text-[var(--green)]">
              <Check className="h-3 w-3" aria-hidden />
              Resolved{resolverName ? ` by ${resolverName}` : ''}
              {comment.resolvedAt ? ` · ${formatAge(comment.resolvedAt)}` : ''}
            </span>
          )}
        </div>

        {editing && editable ? (
          <Composer
            inputId={`edit-${comment.id}`}
            members={members}
            placeholder="Edit your comment…"
            submitLabel="Save"
            initialText={editable.text}
            initialPicked={editable.picked}
            autoFocus
            rows={3}
            onSubmit={(text, picked) => onSaveEdit(comment, text, picked)}
            onCancel={onCancelEdit}
          />
        ) : (
          <div className={resolved ? 'opacity-75' : ''}>
            <CommentBody body={comment.body} />
          </div>
        )}

        {!editing && canComment && (
          <div className="mt-2 flex items-center gap-3">
            {onReply && action('Reply', <Reply className="h-3 w-3" />, onReply)}
            {onToggleResolved &&
              (resolved
                ? action('Reopen', <RotateCcw className="h-3 w-3" />, onToggleResolved)
                : action('Resolve', <Check className="h-3 w-3" />, onToggleResolved))}
            {isOwn && (
              <>
                {action('Edit', <Pencil className="h-3 w-3" />, () => onStartEdit(comment.id))}
                {action('Delete', <X className="h-3 w-3" />, () => onDelete(comment.id), 'danger')}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// EntityCommentsTab
// ============================================================================

export function EntityCommentsTab({
  entityType,
  entityId,
  workspaceId,
  canComment = true,
  className = '',
  anchor: anchorProp = null,
}: EntityCommentsTabProps) {
  const userId = useUserStore((s) => s.userId) || ''
  const userName = useUserStore((s) => s.userName) || 'Anonymous'
  const [searchParams, setSearchParams] = useSearchParams()

  const allComments = useEntityCommentsStore((s) => s.comments)
  const fetchForEntity = useEntityCommentsStore((s) => s.fetchForEntity)
  const addComment = useEntityCommentsStore((s) => s.addComment)
  const updateComment = useEntityCommentsStore((s) => s.updateComment)
  const deleteComment = useEntityCommentsStore((s) => s.deleteComment)
  const setThreadResolved = useEntityCommentsStore((s) => s.setThreadResolved)

  useEffect(() => {
    if (entityId) void fetchForEntity(entityType, entityId)
  }, [entityType, entityId, fetchForEntity])

  // ── People who can be named: the workspace's members, minus me ──────────
  const [members, setMembers] = useState<MentionCandidate[]>([])
  useEffect(() => {
    if (!workspaceId) return
    let live = true
    listWorkspaceMembers(workspaceId)
      .then((list) => {
        if (!live) return
        setMembers(
          list
            .filter((m) => m.userId && m.userId !== userId)
            .map((m) => ({ id: m.userId, name: mentionDisplayName(m.user), email: m.user.email || undefined })),
        )
      })
      .catch(() => {
        if (live) setMembers([])
      })
    return () => {
      live = false
    }
  }, [workspaceId, userId])

  // ── The anchor the composer starts on ───────────────────────────────────
  const anchorParam = searchParams.get('anchor')
  const [anchor, setAnchor] = useState<CommentAnchor | null>(() => anchorFromParam(anchorParam) ?? anchorProp)
  useEffect(() => {
    const fromUrl = anchorFromParam(anchorParam)
    if (fromUrl) setAnchor(fromUrl)
  }, [anchorParam])
  const clearAnchor = useCallback(() => {
    setAnchor(null)
    if (searchParams.has('anchor')) {
      setSearchParams(
        (prev) => {
          prev.delete('anchor')
          return prev
        },
        { replace: true },
      )
    }
  }, [searchParams, setSearchParams])

  // ── Threads ─────────────────────────────────────────────────────────────
  const entityComments = useMemo(
    () => allComments.filter((c) => c.entityType === entityType && c.entityId === entityId),
    [allComments, entityType, entityId],
  )
  const roots = useMemo(
    () =>
      entityComments
        .filter((c) => !c.replyToId)
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [entityComments],
  )
  const repliesByRoot = useMemo(() => {
    const map = new Map<string, EntityComment[]>()
    for (const c of entityComments) {
      if (!c.replyToId) continue
      const list = map.get(c.replyToId) ?? []
      list.push(c)
      map.set(c.replyToId, list)
    }
    for (const list of map.values()) {
      list.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
    }
    return map
  }, [entityComments])

  const counts = useMemo(() => {
    let open = 0
    let resolved = 0
    for (const r of roots) {
      if (r.resolvedAt) resolved += 1
      else open += 1
    }
    return { open, resolved }
  }, [roots])

  const [filter, setFilter] = useState<ThreadFilter>('open')
  const shownRoots = useMemo(
    () =>
      roots.filter((r) =>
        filter === 'all' ? true : filter === 'open' ? !r.resolvedAt : !!r.resolvedAt,
      ),
    [roots, filter],
  )

  // ── Landing on one comment ──────────────────────────────────────────────
  const highlightId = searchParams.get('comment')
  const [flashId, setFlashId] = useState<string | null>(null)
  const landedRef = useRef<string | null>(null)
  useEffect(() => {
    if (!highlightId || landedRef.current === highlightId) return
    const target = entityComments.find((c) => c.id === highlightId)
    if (!target) return
    const root = target.replyToId ? entityComments.find((c) => c.id === target.replyToId) : target
    if (root?.resolvedAt && filter === 'open') {
      setFilter('all')
      return // re-run once the thread is on screen
    }
    landedRef.current = highlightId
    setFlashId(highlightId)
    requestAnimationFrame(() => {
      document.getElementById(`comment-${highlightId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    })
    const timer = setTimeout(() => setFlashId(null), 2_500)
    return () => clearTimeout(timer)
  }, [highlightId, entityComments, filter])

  const resolverIds = useMemo(
    () => roots.map((r) => r.resolvedBy).filter((id): id is string => !!id),
    [roots],
  )
  const resolverNames = useActorDisplayMap(resolverIds)

  // ── Actions ─────────────────────────────────────────────────────────────
  const [replyingTo, setReplyingTo] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)

  const post = useCallback(
    (text: string, picked: MentionCandidate[], replyToId?: string) => {
      if (!userId) return
      const { body, mentions } = serializeMentions(text, picked)
      addComment({
        entityType,
        entityId,
        workspaceId,
        body,
        mentions,
        anchor: replyToId ? null : anchor,
        authorId: userId,
        authorName: userName,
        replyToId,
      })
      if (!replyToId && anchor) clearAnchor()
      if (replyToId) setReplyingTo(null)
    },
    [userId, userName, entityType, entityId, workspaceId, anchor, addComment, clearAnchor],
  )

  const saveEdit = useCallback(
    (comment: EntityComment, text: string, picked: MentionCandidate[]) => {
      const { body, mentions } = serializeMentions(text, picked)
      updateComment(comment.id, body, mentions)
      setEditingId(null)
    },
    [updateComment],
  )

  const remove = useCallback(
    (id: string) => {
      if (window.confirm('Delete this comment?')) deleteComment(id)
    },
    [deleteComment],
  )

  const toggleResolved = useCallback(
    (root: EntityComment) => {
      setThreadResolved(root.id, !root.resolvedAt, userId)
    },
    [setThreadResolved, userId],
  )

  const filterChip = (value: ThreadFilter, label: string, count?: number) => (
    <button
      type="button"
      onClick={() => setFilter(value)}
      aria-pressed={filter === value}
      className={`px-2 py-0.5 text-xs ${
        filter === value
          ? 'bg-[var(--hover-tint)] text-[var(--text-pri)]'
          : 'text-[var(--text-ter)] hover:text-[var(--text-pri)]'
      }`}
    >
      {label}
      {count !== undefined && <span className="ml-1 text-[var(--text-ter)]">{count}</span>}
    </button>
  )

  return (
    <div className={`space-y-5 ${className}`}>
      {canComment && (
        <Composer
          inputId={`ec-input-${entityId}`}
          members={members}
          placeholder={anchor ? `Ask or say something about ${anchor.label ?? anchor.key}…` : 'Add a comment…'}
          submitLabel="Post"
          anchor={anchor}
          onClearAnchor={clearAnchor}
          onSubmit={(text, picked) => post(text, picked)}
        />
      )}

      {roots.length > 0 && (
        <div className="flex items-center gap-1 border-b border-[var(--rule)] pb-1">
          {filterChip('open', 'Open', counts.open)}
          {filterChip('resolved', 'Resolved', counts.resolved)}
          {filterChip('all', 'All')}
        </div>
      )}

      {shownRoots.length === 0 ? (
        <div className="py-8 text-center text-[var(--text-ter)]">
          <MessageSquare className="mx-auto mb-2 h-8 w-8" aria-hidden />
          {roots.length === 0 ? (
            <p className="text-sm">No comments yet</p>
          ) : filter === 'open' ? (
            <p className="text-sm">Every thread is resolved</p>
          ) : (
            <p className="text-sm">Nothing resolved yet</p>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {shownRoots.map((root) => {
            const replies = repliesByRoot.get(root.id) ?? []
            return (
              <div key={root.id} className="space-y-3">
                <CommentRow
                  comment={root}
                  userId={userId}
                  canComment={canComment}
                  members={members}
                  resolverName={root.resolvedBy ? resolverNames[root.resolvedBy] ?? null : null}
                  highlighted={flashId === root.id}
                  editing={editingId === root.id}
                  onStartEdit={setEditingId}
                  onCancelEdit={() => setEditingId(null)}
                  onSaveEdit={saveEdit}
                  onDelete={remove}
                  onReply={() => {
                    setReplyingTo(root.id)
                    setTimeout(() => document.getElementById(`reply-${root.id}`)?.focus(), 0)
                  }}
                  onToggleResolved={() => toggleResolved(root)}
                />

                {(replies.length > 0 || replyingTo === root.id) && (
                  <div className="ml-11 space-y-3 border-l-2 border-[var(--rule)] pl-4">
                    {replies.map((reply) => (
                      <CommentRow
                        key={reply.id}
                        comment={reply}
                        isReply
                        userId={userId}
                        canComment={canComment}
                        members={members}
                        highlighted={flashId === reply.id}
                        editing={editingId === reply.id}
                        onStartEdit={setEditingId}
                        onCancelEdit={() => setEditingId(null)}
                        onSaveEdit={saveEdit}
                        onDelete={remove}
                        onReply={() => {
                          setReplyingTo(root.id)
                          setTimeout(() => document.getElementById(`reply-${root.id}`)?.focus(), 0)
                        }}
                      />
                    ))}
                    {replyingTo === root.id && canComment && (
                      <Composer
                        inputId={`reply-${root.id}`}
                        members={members}
                        placeholder={`Reply to ${root.authorName}…`}
                        submitLabel="Reply"
                        autoFocus
                        onSubmit={(text, picked) => post(text, picked, root.id)}
                        onCancel={() => setReplyingTo(null)}
                      />
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default EntityCommentsTab
